import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useState } from 'react';

// Using the canonical AppointmentType from ehr-gateway with additional id field for UI
interface AppointmentType {
  id: string;
  ehrId: string;
  name: string;
  abbreviation: string;
  durationCategory: 'short' | 'medium' | 'long' | 'other';
  ehrSystem?: string;
  organizationId?: string;
}

interface EditAppointmentTypeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (appointmentType: AppointmentType) => void;
  appointmentType: AppointmentType | null;
  isNew: boolean;
  isLoading?: boolean;
}

export function EditAppointmentTypeDialog({
  isOpen,
  onClose,
  onSave,
  appointmentType,
  isNew,
  isLoading = false,
}: EditAppointmentTypeDialogProps) {
  // Form state
  const [ehrId, setEhrId] = useState('');
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [durationCategory, setDurationCategory] = useState<'short' | 'medium' | 'long' | 'other'>(
    'medium'
  );

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Confirmation dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<AppointmentType | null>(null);

  // Initialize form when dialog opens or appointment type changes
  useEffect(() => {
    if (isOpen && appointmentType) {
      setEhrId(appointmentType.ehrId);
      setName(appointmentType.name);
      setAbbreviation(appointmentType.abbreviation);
      setDurationCategory(appointmentType.durationCategory);
      setErrors({});
    } else if (isOpen && isNew) {
      // Reset form for new appointment type
      setEhrId('');
      setName('');
      setAbbreviation('');
      setDurationCategory('medium');
      setErrors({});
    }
  }, [isOpen, appointmentType, isNew]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (isNew && !ehrId.trim()) {
      newErrors.ehrId = 'EHR ID is required';
    }
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!abbreviation.trim()) {
      newErrors.abbreviation = 'Abbreviation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const appointmentTypeData: AppointmentType = {
      id: appointmentType?.id || '',
      ehrId: ehrId.trim(),
      name: name.trim(),
      abbreviation: abbreviation.trim(),
      durationCategory,
    };

    // Store pending changes and show confirmation
    setPendingChanges(appointmentTypeData);
    setShowConfirm(true);
  };

  const handleConfirmSave = () => {
    if (pendingChanges) {
      onSave(pendingChanges);
      setShowConfirm(false);
      setPendingChanges(null);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingChanges(null);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? 'Add New Appointment Type' : 'Edit Appointment Type'}
            </DialogTitle>
            <DialogDescription>
              {isNew
                ? 'Create a new appointment type for this organization.'
                : 'Update the details of this appointment type.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* EHR ID Field - only editable for new appointment types */}
            {isNew && (
              <div className="grid gap-2">
                <Label htmlFor="ehrId">EHR ID</Label>
                <Input
                  id="ehrId"
                  value={ehrId}
                  onChange={(e) => setEhrId(e.target.value)}
                  placeholder="e.g., 1234"
                  disabled={isLoading}
                />
                {errors.ehrId && <span className="text-sm text-destructive">{errors.ehrId}</span>}
              </div>
            )}

            {/* Name Field - Read-only for existing, editable for new */}
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => isNew && setName(e.target.value)}
                placeholder="e.g., Annual Eye Exam"
                disabled={isLoading || !isNew}
                className={!isNew ? 'bg-muted' : ''}
              />
              {errors.name && <span className="text-sm text-destructive">{errors.name}</span>}
              {!isNew && (
                <span className="text-xs text-muted-foreground">
                  Name comes from EHR and cannot be edited
                </span>
              )}
            </div>

            {/* Abbreviation Field - Read-only for existing, editable for new */}
            <div className="grid gap-2">
              <Label htmlFor="abbreviation">Abbreviation</Label>
              <Input
                id="abbreviation"
                value={abbreviation}
                onChange={(e) => isNew && setAbbreviation(e.target.value)}
                placeholder="e.g., AEE"
                disabled={isLoading || !isNew}
                className={!isNew ? 'bg-muted' : ''}
              />
              {errors.abbreviation && (
                <span className="text-sm text-destructive">{errors.abbreviation}</span>
              )}
              {!isNew && (
                <span className="text-xs text-muted-foreground">
                  Abbreviation comes from EHR and cannot be edited
                </span>
              )}
            </div>

            {/* Duration Category Field */}
            <div className="grid gap-2">
              <Label htmlFor="durationCategory">Duration Category</Label>
              <Select
                value={durationCategory}
                onValueChange={(value) =>
                  setDurationCategory(value as 'short' | 'medium' | 'long' | 'other')
                }
                disabled={isLoading}
              >
                <SelectTrigger id="durationCategory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="long">Long</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Confirm Changes</DialogTitle>
            <DialogDescription>
              {isNew ? (
                <div className="space-y-2 pt-2">
                  <p>Are you sure you want to create this appointment type?</p>
                  <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                    <div>
                      <strong>EHR ID:</strong> {pendingChanges?.ehrId}
                    </div>
                    <div>
                      <strong>Name:</strong> {pendingChanges?.name}
                    </div>
                    <div>
                      <strong>Duration:</strong> {pendingChanges?.durationCategory}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 pt-2">
                  <p>Are you sure you want to save these changes?</p>
                  <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                    <div>
                      <strong>Duration:</strong> {pendingChanges?.durationCategory}
                    </div>
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelConfirm}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSave}>{isNew ? 'Create' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
